import { useMemo } from 'react'
import type { ProposalTemplateType } from 'types/proposalTemplate'
import type { TemplateLibraryCardProps } from '../sections/proposal/templateLibrary/TemplateLibraryCard'

export type CardRenderDataByTemplateData = {
  isPlaceholderDefault: false
  templateData: ProposalTemplateType
  title: string
}

export type CardRenderDataByDefaultData = {
  isPlaceholderDefault: true
  metaData: Omit<TemplateLibraryCardProps, 'selected' | 'disabled' | 'onSelect'>
}

export type CardRenderData = CardRenderDataByTemplateData | CardRenderDataByDefaultData

export const findProjectLevelTemplateByOrgLevelTemplate = ({
  orgLevelTemplate,
  allProjectLevelTemplates,
}: {
  orgLevelTemplate: ProposalTemplateType
  allProjectLevelTemplates: ProposalTemplateType[]
}): ProposalTemplateType | undefined => {
  if (allProjectLevelTemplates === undefined) return undefined
  // get most recent project level template that is created from the org level template
  return allProjectLevelTemplates.find((projectLevelTemplate) => {
    return projectLevelTemplate.original === orgLevelTemplate.url
  })
}

const checkIfProposalV2ConfigIsUpdated = ({
  orgLevelTemplate,
  projectLevelTemplate,
}: {
  orgLevelTemplate: ProposalTemplateType
  projectLevelTemplate: ProposalTemplateType
}): boolean => {
  const projectTemplateModifiedDate = new Date(projectLevelTemplate.modified_date)
  const orgProposalConfigModifiedDateString = orgLevelTemplate.proposal_v2_config?.config_updated_date

  if (!orgProposalConfigModifiedDateString || !projectTemplateModifiedDate) return false

  const orgProposalConfigModifiedDate = new Date(orgProposalConfigModifiedDateString)

  return orgProposalConfigModifiedDate > projectTemplateModifiedDate
}

const checkIfTemplateSettingsIsUpdated = ({
  orgLevelTemplate,
  projectLevelTemplate,
}: {
  orgLevelTemplate: ProposalTemplateType
  projectLevelTemplate: ProposalTemplateType
}): boolean => {
  const projectTemplateModifiedDate = new Date(projectLevelTemplate.modified_date)
  const orgTemplateSettingsModifiedDateString =
    orgLevelTemplate.settings && JSON.parse(orgLevelTemplate.settings).modified_date
  if (!orgTemplateSettingsModifiedDateString || !projectTemplateModifiedDate) return false

  const orgTemplateSettingsModifiedDate = new Date(orgTemplateSettingsModifiedDateString)
  return orgTemplateSettingsModifiedDate > projectTemplateModifiedDate
}

// Used to show an "Updated" tag
const checkIfOrgLevelTemplateIsUpdated = ({
  orgLevelTemplate,
  projectLevelTemplate,
}: {
  orgLevelTemplate: ProposalTemplateType
  projectLevelTemplate: ProposalTemplateType
}): boolean => {
  const orgTemplateModifiedDate = new Date(orgLevelTemplate.modified_date)
  const projectTemplateModifiedDate = new Date(projectLevelTemplate.modified_date)

  // early break
  if (projectTemplateModifiedDate > orgTemplateModifiedDate) {
    return false
  }

  if (checkIfProposalV2ConfigIsUpdated({ orgLevelTemplate, projectLevelTemplate })) {
    return true
  }

  if (checkIfTemplateSettingsIsUpdated({ orgLevelTemplate, projectLevelTemplate })) {
    return true
  }

  return false
}

const getProposalV2TemplateCardsToRender = (
  projectLevelSMTemplates?: ProposalTemplateType[],
  orgLevelSMTemplates?: ProposalTemplateType[]
): CardRenderData[] => {
  const templatesToRender: CardRenderData[] = []

  // Include default SMP template card
  const defaultCardData: CardRenderDataByDefaultData = {
    isPlaceholderDefault: true,
    metaData: {
      id: 'grapes-swipe-template-card',
      tag: '',
      header: '',
      body: '',
      imgSrc: `${window.PUBLIC_URL}/images/proposal/template_default.png`,
    },
  }
  templatesToRender.push(defaultCardData)

  orgLevelSMTemplates?.forEach((orgTemplate: ProposalTemplateType) => {
    const existingProjectTemplate = findProjectLevelTemplateByOrgLevelTemplate({
      allProjectLevelTemplates: projectLevelSMTemplates ?? [],
      orgLevelTemplate: orgTemplate,
    })

    if (
      existingProjectTemplate &&
      checkIfOrgLevelTemplateIsUpdated({
        orgLevelTemplate: orgTemplate,
        projectLevelTemplate: existingProjectTemplate,
      })
    ) {
      const clonedOrgTemplate = { ...orgTemplate }
      clonedOrgTemplate.is_updated = true
      clonedOrgTemplate.proposal_v2_config = orgTemplate.proposal_v2_config
      templatesToRender.push({
        isPlaceholderDefault: false,
        templateData: clonedOrgTemplate,
        title: orgTemplate.title,
      })
    }

    if (existingProjectTemplate) {
      templatesToRender.push({
        isPlaceholderDefault: false,
        templateData: {
          ...existingProjectTemplate,
          proposal_image_file_contents: orgTemplate.proposal_image_file_contents,
        },
        title: orgTemplate.title,
      })
    } else {
      templatesToRender.push({
        isPlaceholderDefault: false,
        templateData: orgTemplate,
        title: orgTemplate.title,
      })
    }
  })

  return templatesToRender
}

const getClassicTemplateCardsToRender = (orgLvlClassicTemplates?: ProposalTemplateType[]): CardRenderData[] => {
  const classicCardData: CardRenderDataByDefaultData = {
    isPlaceholderDefault: true,
    metaData: {
      id: 'classic-template-card',
      tag: '',
      header: '',
      body: '',
      imgSrc: `${window.PUBLIC_URL}/images/proposal/template_classic.png`,
    },
  }

  if (orgLvlClassicTemplates === undefined || orgLvlClassicTemplates.length === 0) {
    return [classicCardData]
  }

  return (
    orgLvlClassicTemplates?.map((template) => ({
      isPlaceholderDefault: false,
      templateData: template,
      title: template.title,
    })) ?? []
  )
}

export const useTemplateCards = (
  projectLevelSMTemplates?: ProposalTemplateType[],
  orgLevelSMTemplates?: ProposalTemplateType[],
  orgLevelClassicTemplates?: ProposalTemplateType[]
) => {
  const proposalV2CardsData = useMemo(() => {
    return getProposalV2TemplateCardsToRender(projectLevelSMTemplates, orgLevelSMTemplates)
  }, [projectLevelSMTemplates, orgLevelSMTemplates])

  const classicCardsData = useMemo(() => {
    return getClassicTemplateCardsToRender(orgLevelClassicTemplates)
  }, [orgLevelClassicTemplates])

  return { proposalV2CardsData, classicCardsData }
}
